import { useEffect, useRef } from 'react';

// Full-screen zoomed view of one gallery tile, shared by the signed-in
// Gallery and the public /gallery share pages. Pages through the grid
// without closing: bold side arrows, arrow keys, trackpad horizontal
// scroll, and touch swipe all step; Esc, the ✕ button, or a click on the
// backdrop close. Tiles without a resolved photo are skipped, so paging
// only ever lands on a real image.
//
// `index` is the tile's position in `tiles`; `urlFor(tile)` returns the
// tile's resolved front-photo URL (or null/undefined while missing);
// `captionFor(tile)` returns the caption line.
export default function GalleryLightbox({ tiles, index, urlFor, captionFor, onNavigate, onClose }) {
  const touchX = useRef(null);
  const wheelLock = useRef(0);

  const viewable = tiles.reduce((acc, t, i) => { if (urlFor(t)) acc.push(i); return acc; }, []);
  const pos = viewable.indexOf(index);
  const step = (dir) => {
    if (viewable.length < 2) return;
    onNavigate(viewable[(pos + dir + viewable.length) % viewable.length]);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') step(-1);
      else if (e.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const tile = tiles[index];
  if (!tile) return null;

  // Thumb-friendly tap target (48px+) with a heavy glyph and gold border so
  // it stays visible over bright card art on small screens.
  const arrowStyle = (side) => ({
    position: 'fixed',
    [side]: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '52px',
    height: '92px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.78)',
    border: '2px solid var(--gold)',
    borderRadius: '5px',
    color: 'var(--gold)',
    fontFamily: "'Orbitron', monospace",
    fontWeight: 900,
    fontSize: '30px',
    lineHeight: 1,
    padding: 0,
    cursor: 'pointer',
    boxShadow: '0 0 12px rgba(0,0,0,0.8)',
    WebkitTapHighlightColor: 'transparent',
  });

  return (
    <div
      onClick={onClose}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        touchX.current = null;
        if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
      }}
      onWheel={(e) => {
        // Trackpad horizontal scroll pages too; the cooldown keeps one
        // physical swipe (which fires many wheel events) to one step.
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < 12) return;
        const now = Date.now();
        if (now - wheelLock.current < 400) return;
        wheelLock.current = now;
        step(e.deltaX > 0 ? 1 : -1);
      }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px', cursor: 'zoom-out', touchAction: 'none' }}>
      <img src={urlFor(tile)} alt={tile.card.name} onClick={e => e.stopPropagation()} style={{ maxHeight: '80vh', maxWidth: '90vw', objectFit: 'contain', borderRadius: '4px' }} />
      <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--muted-3)', textAlign: 'center', padding: '0 70px' }}>
        {captionFor(tile)}
        {viewable.length > 1 && (
          <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '6px', letterSpacing: '1px' }}>{pos + 1} / {viewable.length}</div>
        )}
      </div>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 'calc(20px + max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px)))', right: '20px', background: 'none', border: '1px solid var(--border)', color: 'var(--muted-2)', cursor: 'pointer', fontSize: '16px', width: '36px', height: '36px', borderRadius: '2px' }}>
        ✕
      </button>
      {viewable.length > 1 && (
        <>
          <button aria-label="Previous card" onClick={(e) => { e.stopPropagation(); step(-1); }} style={arrowStyle('left')}>❮</button>
          <button aria-label="Next card" onClick={(e) => { e.stopPropagation(); step(1); }} style={arrowStyle('right')}>❯</button>
        </>
      )}
    </div>
  );
}
